// POS PIN login — hibrid bootstrap axını.
//
// Cloud işçi PIN-lərini pos_local-a SYNC ETMİR (StreamPullService pinHash=null),
// ona görə pos_local sync olunan işçinin PIN-ini lokal yoxlaya bilmir. Həll:
//
//   ONLINE:  PIN → cloud /auth/staff-login (bcrypt doğrulama, real access_token)
//                → uğurda pos_local /v1/auth/provision (lokal scrypt hash yazılır
//                  + pos_token verilir). Sonrakı girişlər offline işləyir.
//   OFFLINE: cloud-a çatmırsa → pos_local /v1/auth/login (son məlum lokal hash)
//                → pos_token. Cloud access_token yoxdur (admin offline gözlənilən).
//
// Bu modul cloud-a birbaşa `fetch` ilə gedir (axios YOX) — əks halda interceptor
// (install.ts) eyni staff-login route-unu yenidən tutub sonsuz rekursiya yaradar.

import { cloudApi } from './deviceSession';
import { posRequest, setPosToken } from './posClient';

export interface StaffLoginBody {
  staff_id?: number | string;
  pin?: string;
  device_token?: string;
}

/** Adapter-uyğun xəta (adapter.ts err.posStatus/posCode oxuyur → axios response). */
function posError(status: number, message: string, code: string): Error {
  const e = new Error(message) as Error & { posStatus?: number; posCode?: string };
  e.posStatus = status;
  e.posCode = code;
  return e;
}

/**
 * Cloud staff name == pos_local display_name (hər ikisi cloud users-dən).
 * restaurant-login cavabındakı staff_list-i (localStorage) id ilə axtarırıq —
 * pos_local /v1/auth/* ad ilə işləyir, front isə yalnız staff_id göndərir.
 */
function staffDisplayName(staffId: number | string | undefined): string | null {
  if (staffId === undefined || staffId === null) return null;
  try {
    const raw = localStorage.getItem('staff_list');
    if (!raw) return null;
    const list = JSON.parse(raw) as Array<{ id: number | string; name?: string }>;
    const hit = list.find((s) => String(s.id) === String(staffId));
    return hit?.name ?? null;
  } catch {
    return null;
  }
}

interface PosLoginData {
  token: string;
}

/** Cloud-a çatmadıqda: lokal son-məlum PIN ilə giriş (offline-first POS). */
async function offlineLocalLogin(
  displayName: string | null,
  pin: string,
): Promise<{ status: number; data: unknown }> {
  if (!displayName) {
    throw posError(503, 'Offline: işçi tanınmadı (əvvəlcə online giriş lazımdır)', 'OFFLINE_NO_STAFF');
  }
  const r = await posRequest<PosLoginData>({
    method: 'POST',
    path: '/v1/auth/login',
    body: { displayName, pin },
    token: null,
  });
  if (!r.ok || !r.data?.token) {
    const status = (r as { _status?: number })._status === 423 ? 423 : 401;
    throw posError(status, r.error?.message || 'Yanlış PIN (offline)', r.error?.code || 'OFFLINE_LOGIN_FAILED');
  }
  setPosToken(r.data.token);
  // Front access_token-i localStorage `token`-ə yazır və /masalar-a keçir. Offline
  // cloud token yoxdur → pos_token-i yer tutucu kimi qoyuruq; cloud çağırışları
  // onsuz da offline uğursuzdur, POS çağırışları ayrı pos_token istifadə edir.
  return { status: 200, data: { access_token: r.data.token, offline: true } };
}

/**
 * `POST /auth/staff-login` üçün gateway handler. Cloud cavabını (cloud shape)
 * qaytarır + yan-təsir kimi pos_token provision edir.
 */
export async function runStaffLogin(
  body: StaffLoginBody,
): Promise<{ status: number; data: unknown }> {
  const displayName = staffDisplayName(body.staff_id);
  const pin = String(body.pin ?? '');

  // Köhnə POS sessiyasını təmizlə (işçi dəyişimi).
  setPosToken(null);

  // ── Addım 1: cloud PIN-i doğrulasın (authoritative) ──────────────────────
  let cloudJson: unknown = null;
  let reachedCloud = false;
  try {
    // Cihazın ÖZ bulud ünvanı — `base_url` sabit yazıldığı üçün quraşdırma
    // kodunda başqa ünvan göstərilibsə PIN səhv yerdə yoxlanardı.
    const res = await fetch(`${cloudApi()}/auth/staff-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    reachedCloud = true;
    cloudJson = await res.json().catch(() => null);
    if (!res.ok) {
      // ── BULUDUN RƏDDİ İLƏ NASAZLIĞINI AYIR ──
      //
      // 401/403/404 = həqiqi rədd: PIN yanlışdır, hesab bağlıdır, işçi yoxdur.
      // Bunlarda yerli yoxlamaya keçmək TƏHLÜKƏSİZLİK DELİYİ olardı — bulud
      // "yox" deyəndə cihaz "hə" deməməlidir.
      //
      // 429/5xx = buludun ÖZ problemi (limit, server xətası). Burada girişi
      // bloklamağın heç bir təhlükəsizlik faydası yoxdur, zərəri isə birbaşadır:
      // restoran işləyə bilmir. Məhz belə oldu — PIN limiti restoranın bütün
      // terminalları üçün ümumi IP-yə görə sayılırdı və növbə dəyişəndə hamı
      // 429 alırdı. Cihaz işçini yerli hash ilə tanıyırsa girişə icazə verilir.
      const msg = (cloudJson as { message?: string } | null)?.message || 'Giriş uğursuz';
      const rejected = res.status === 401 || res.status === 403 || res.status === 404;
      if (rejected) {
        throw posError(res.status, msg, 'CLOUD_AUTH_FAILED');
      }
      return offlineLocalLogin(displayName, pin);
    }
  } catch (e) {
    if (reachedCloud) throw e; // həqiqi auth rəddi → ötür
    // Şəbəkə xətası → offline fallback.
    return offlineLocalLogin(displayName, pin);
  }

  // ── Addım 2: cloud OK → pos_local-a provision (lokal hash + pos_token) ────
  //
  // BU ADDIM MƏCBURİDİR, "best-effort" DEYİL.
  //
  // Əvvəl provision uğursuz olsa da giriş uğurlu sayılırdı. Nəticə yarımçıq
  // sessiya idi: bulud token-i var, POS sessiyası yox. İşçi daxil olmuş
  // görünürdü, amma masa/sifariş/printer sorğularının hamısı 401 verirdi və
  // səbəb heç yerdə yazılmırdı.
  //
  // POS terminalında POS sessiyası olmadan giriş MƏNASIZDIR — işin özü
  // pos_local-dadır. Ona görə alınmasa bu, uğursuz girişdir və işçi səbəbi
  // görməlidir.
  let provisioned = false;
  // ── SƏBƏBİ AYIRD ET ──
  //
  // Əvvəl provision-un HƏR uğursuzluğu eyni mesajı verirdi:
  // "İşçi bu cihazda hələ tanınmır, sinxronlaşma bitənə qədər gözləyin."
  //
  // pos_local ÜMUMİYYƏTLƏ İŞLƏMƏYƏNDƏ (`fetch` xətası) bu mesaj yalnız
  // yanlış deyil, həm də zərərlidir: işçi gözləyir, gözləmək isə heç vaxt
  // kömək etməyəcək. Yerli xidmət qalxmayıbsa yeganə həll tətbiqi yenidən
  // başlatmaqdır — ekran bunu deməlidir.
  //
  // İki hal fərqlidir:
  //   fetch atdı        → pos_local əlçatmaz (xidmət işləmir)
  //   cavab gəldi, rədd → işçi yerli bazada yoxdur (sinxron gözlənilir)
  let backendReachable = false;
  if (displayName) {
    try {
      const prov = await posRequest<PosLoginData>({
        method: 'POST',
        path: '/v1/auth/provision',
        body: { displayName, pin },
        token: null,
      });
      // Buraya çatdıqsa pos_local cavab verdi — rədd etsə də işləyir.
      backendReachable = true;
      if (prov.ok && prov.data?.token) {
        setPosToken(prov.data.token);
        provisioned = true;
      }
    } catch {
      // `fetch` yalnız şəbəkə səviyyəsində atır: ECONNREFUSED, port bağlı,
      // proses çöküb. HTTP 4xx/5xx buraya DÜŞMÜR.
      backendReachable = false;
    }
  }

  if (!provisioned) {
    if (!backendReachable) {
      throw posError(
        503,
        'POS xidməti işləmir. Proqramı bağlayıb yenidən açın; problem davam edərsə dəstəyə müraciət edin.',
        'POS_BACKEND_DOWN',
      );
    }
    // pos_local işləyir, amma bu işçini tanımır: təzə quraşdırılmış terminal
    // və ya paneldə yeni əlavə edilmiş işçi. Burada gözləmək HƏQİQƏTƏN kömək edir.
    throw posError(
      503,
      'İşçi bu cihazda hələ tanınmır. Sinxronlaşma bitənə qədər gözləyin və yenidən cəhd edin.',
      'POS_PROVISION_FAILED',
    );
  }

  return { status: 200, data: cloudJson };
}
